import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Edit,
  History,
  Printer,
  Book as BookIcon,
  Package,
  DollarSign,
  Calendar,
  Truck,
  User as UserIcon,
  Clock,
  CheckCircle
} from "lucide-react";
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { PrintRunHistory } from '@/api/entities';
import { formatAddress } from '@/utils';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  planned: 'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
  in_production: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const statusIcons = {
  draft: Clock,
  planned: Clock,
  in_progress: Printer,
  in_production: Printer,
  completed: CheckCircle,
  delivered: CheckCircle,
  cancelled: Clock
};

const typeLabels = {
    main_print: 'Основной тираж',
    reprint: 'Допечатка'
};

export default function ViewPrintRunModal({ isOpen, onClose, onEdit, printRun, book, warehouse }) {
    const { t } = useTranslation();
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    useEffect(() => {
        if (printRun && isOpen) {
            loadHistory();
        }
    }, [printRun, isOpen]);

    const loadHistory = async () => {
        if (!printRun) return;
        setIsLoadingHistory(true);
        try {
            const historyData = await PrintRunHistory.get(printRun.id);
            setHistory(historyData || []);
        } catch (error) {
            console.error("Ошибка загрузки истории:", error);
            setHistory([]);
        }
        setIsLoadingHistory(false);
    };

    if (!isOpen || !printRun) {
        return null;
    }

    const StatusIcon = statusIcons[printRun.status] || Clock;
    const totalCost = (printRun.cost_per_unit || 0) * (printRun.quantity || 0);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader className="flex flex-row items-center justify-between">
                    <DialogTitle className="text-xl font-bold text-slate-900">
                        {t('print_run_view.title', { number: printRun.document_number })}
                        <p className="text-sm font-normal text-slate-500 mt-1">
                            {t('print_run_view.date_from', { date: format(new Date(printRun.document_date), 'dd MMMM yyyy', { locale: ru }) })}
                        </p>
                    </DialogTitle>
                    <Button onClick={() => onEdit(printRun)} className="bg-amber-600 hover:bg-amber-700">
                        <Edit className="w-4 h-4 mr-2" />
                        {t('print_run_view.edit')}
                    </Button>
                </DialogHeader>

                <Tabs defaultValue="details" className="w-full mt-4">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="details">{t('print_run_view.tabs.details')}</TabsTrigger>
                        <TabsTrigger value="history">{t('print_run_view.tabs.history')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="space-y-6 mt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Основная информация */}
                            <div className="lg:col-span-2">
                                <Card className="elegant-card">
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <BookIcon className="w-5 h-5 text-slate-600" />
                                            Основная информация
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Книга</p>
                                                <p className="font-semibold text-slate-900">{book?.title || 'Неизвестная книга'}</p>
                                                <p className="text-sm text-slate-600">{book?.author || ''}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">{t('print_run_view.type')}</p>
                                                <Badge variant="outline" className={printRun.type === 'main_print' ? 'border-blue-200 text-blue-800' : 'border-purple-200 text-purple-800'}>
                                                    {printRun.type === 'main_print' ? t('print_run_form.main_print') : t('print_run_form.reprint')}
                                                </Badge>
                                            </div>
                                        </div>
                                        
                                        {printRun.isbn && (
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">ISBN</p>
                                                <p className="font-mono text-slate-900">{printRun.isbn}</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card className="elegant-card mt-6">
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Package className="w-5 h-5 text-slate-600" />
                                            Производство и доставка
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Количество</p>
                                                <p className="text-2xl font-bold text-slate-900">{printRun.quantity?.toLocaleString()} экз.</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Склад назначения</p>
                                                <p className="font-semibold text-slate-900">{warehouse?.name || 'Неизвестный склад'}</p>
                                                <p className="text-sm text-slate-600">
                                                    {formatAddress(warehouse?.address, '')}
                                                </p>
                                            </div>
                                        </div>

                                        {printRun.expected_delivery_date && (
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Ожидаемая дата поставки</p>
                                                <p className="font-semibold text-slate-900 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    {format(new Date(printRun.expected_delivery_date), 'dd MMMM yyyy', { locale: ru })}
                                                </p>
                                            </div>
                                        )}

                                        {printRun.actual_delivery_date && (
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Фактическая дата доставки</p>
                                                <p className="font-semibold text-green-600 flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4" />
                                                    {format(new Date(printRun.actual_delivery_date), 'dd MMMM yyyy', { locale: ru })}
                                                </p>
                                            </div>
                                        )}

                                        {printRun.notes && (
                                            <div>
                                                <p className="text-sm font-medium text-slate-500">Примечания</p>
                                                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{printRun.notes}</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Статус и финансы */}
                            <div className="space-y-6">
                                <Card className="elegant-card">
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-lg">Статус</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <Badge className={`${statusColors[printRun.status] || statusColors.draft} border font-medium text-base px-3 py-2`}>
                                            <StatusIcon className="w-4 h-4 mr-2" />
                                            {printRun.status === 'draft' ? 'Черновик' :
                                             printRun.status === 'planned' ? 'Запланирован' :
                                             printRun.status === 'in_progress' ? 'В печати' :
                                             printRun.status === 'in_production' ? 'В печати' :
                                             printRun.status === 'completed' ? 'Завершен' :
                                             printRun.status === 'delivered' ? 'Доставлен' :
                                             printRun.status === 'cancelled' ? 'Отменен' :
                                             printRun.status || 'Неизвестно'}
                                        </Badge>
                                    </CardContent>
                                </Card>

                                <Card className="elegant-card">
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <DollarSign className="w-5 h-5 text-slate-600" />
                                            Финансы
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div>
                                            <p className="text-sm font-medium text-slate-500">Себестоимость за экз.</p>
                                            <p className="font-semibold text-slate-900">{(printRun.cost_per_unit || 0).toLocaleString()} ₽</p>
                                        </div>
                                        <div className="pt-3 border-t">
                                            <p className="text-sm font-medium text-slate-500">Общая стоимость</p>
                                            <p className="text-2xl font-bold text-green-600">{totalCost.toLocaleString()} ₽</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="history" className="mt-6">
                        <Card className="elegant-card">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <History className="w-5 h-5 text-slate-600" />
                                    {t('print_run_view.history.title')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {isLoadingHistory ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                                    </div>
                                ) : history.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                        <p className="font-medium">История пуста</p>
                                        <p className="text-sm">{t('print_run_view.history.no_actions')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4 max-h-96 overflow-y-auto">
                                        {history.map((historyItem, index) => (
                                            <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                                                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-slate-900">{historyItem.details}</p>
                                                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                                                        <UserIcon className="w-3 h-3" />
                                                        <span>{historyItem.user_name}</span>
                                                        <span>•</span>
                                                        <span>{format(new Date(historyItem.created_date), 'dd.MM.yyyy в HH:mm', { locale: ru })}</span>
                                                    </div>
                                                    {historyItem.old_value && historyItem.new_value && (
                                                        <div className="text-xs text-slate-500 mt-1">
                                                            <span className="line-through">{historyItem.old_value}</span>
                                                            <span className="mx-2">→</span>
                                                            <span className="font-medium">{historyItem.new_value}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}